// import { useTranslations } from 'next-intl';

'use client';

import { currentAuthOwner, writeAuthCookies } from '@/shared/utils/authCookieOwner';

import { usePathname, useRouter } from 'next/navigation';
import { Suspense, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { captureException, captureMessage, withScope } from '@sentry/nextjs';

import Footer from '@/components/global/Footer';
import Header from '@/components/global/Header';
import {PartnerBanner} from '@/components/global/PartnerBanner';
import { ProgramLinkStateStrip } from '@/components/global/ProgramLinkStateStrip';
import { WalletLinkEntry } from '@/components/global/WalletLinkEntry';
import { ShimmerLogoSVG } from '@/components/ShimmerLogo';
import { useAuthStore } from '@/presentation/hooks/useStores';
import {
    clearShopAuthHandoffPayload,
    extractShopAuthHandoffPayload,
    persistShopAuthHandoffPayload,
    readShopAuthHandoffPayload,
} from '@/shared/utils/shopAuthHandoff';
import { storePostAuthRedirect } from '@/shared/utils/postAuthRedirect';

const ONBOARDING_COMPLETE_PROFILE_PATH = '/onboarding/complete-profile';
const PARTNER_AUTH_PATHS = ['/auth/partner', '/partner/auth'];
const SHOP_HANDOFF_PENDING_KEY = 'motito_shop_handoff_pending';
const SHOP_HANDOFF_SOURCE_KEY = 'motito_shop_handoff_source';
const SHOP_HANDOFF_SOURCE_TTL_MS = 15 * 60 * 1000;
const PLAY_SOURCE_HINTS = new Set(['play', 'motito_play', 'spin_play']);

function normalizePlayHandoffSource(source: string | null | undefined): string | null {
    const normalized = (source || '').trim().toLowerCase();
    if (!normalized) {
        return null;
    }
    return PLAY_SOURCE_HINTS.has(normalized) ? 'play' : normalized;
}

function hasPlaySourceHint(search: string): boolean {
    if (!search) {
        return false;
    }
    const params = new URLSearchParams(search);
    const candidates = [
        params.get('motito_handoff'),
        params.get('source'),
        params.get('src'),
        params.get('from'),
    ];
    return candidates.some((value) =>
        Boolean(value && PLAY_SOURCE_HINTS.has(value.trim().toLowerCase()))
    );
}

function hasPlaySourceHintFromHash(hash: string): boolean {
    if (!hash) {
        return false;
    }
    const normalizedHash = hash.startsWith('#') ? hash.slice(1) : hash;
    return hasPlaySourceHint(normalizedHash);
}

function readStoredHandoffSource(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    const storedSource = window.sessionStorage.getItem(SHOP_HANDOFF_SOURCE_KEY);
    if (!storedSource) {
        return null;
    }
    try {
        const parsed = JSON.parse(storedSource) as { source?: string; ts?: number };
        const source = normalizePlayHandoffSource(parsed.source);
        const ts = Number(parsed.ts || 0);
        if (source && ts > 0 && Date.now() - ts <= SHOP_HANDOFF_SOURCE_TTL_MS) {
            return source;
        }
    } catch {
        // no-op: fall through to cleanup below
    }
    window.sessionStorage.removeItem(SHOP_HANDOFF_SOURCE_KEY);
    return null;
}

function readHandoffPendingFlag(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.sessionStorage.getItem(SHOP_HANDOFF_PENDING_KEY) === '1';
}

export default observer(function Layout(props: { children: React.ReactNode }) {
    // const t = useTranslations('RootLayout');
    const pathname = usePathname();
    const router = useRouter();
    const authStore = useAuthStore();
    const [isOnboardingGateChecking, setIsOnboardingGateChecking] =
        useState(true);
    const [isAuthHandoffProcessing, setIsAuthHandoffProcessing] =
        useState(false);
    const [hasHandoffPending, setHasHandoffPending] = useState(false);
    const [handoffSource, setHandoffSource] = useState<string | null>(null);
    const [hasPlayHintFromHash, setHasPlayHintFromHash] = useState(false);
    const [hasPlayHintFromQuery, setHasPlayHintFromQuery] = useState(false);
    const hasPlayHint = hasPlayHintFromQuery || hasPlayHintFromHash;
    const isCompleteProfilePath = pathname?.startsWith(
        ONBOARDING_COMPLETE_PROFILE_PATH
    );
    const isPartnerAuthPath = PARTNER_AUTH_PATHS.some((path) =>
        pathname?.startsWith(path)
    );

    const clearHandoffPending = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.sessionStorage.removeItem(SHOP_HANDOFF_PENDING_KEY);
        setHasHandoffPending(false);
    }, []);

    const clearHandoffSource = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.sessionStorage.removeItem(SHOP_HANDOFF_SOURCE_KEY);
        setHandoffSource(null);
    }, []);

    useLayoutEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const hasPlayHint = hasPlaySourceHint(window.location.search);
        const hasPlayHashHint = hasPlaySourceHintFromHash(window.location.hash);
        setHasPlayHintFromQuery(hasPlayHint);
        setHasPlayHintFromHash(hasPlayHashHint);
        // Only hash-based hints represent an active handoff signal.
        // Query-string hints are attribution only and must not force handoff mode.
        if (hasPlayHashHint) {
            const payload = JSON.stringify({ source: 'play', ts: Date.now() });
            window.sessionStorage.setItem(SHOP_HANDOFF_SOURCE_KEY, payload);
            setHandoffSource('play');
        }
        setHandoffSource((current) => current ?? readStoredHandoffSource());
        setHasHandoffPending(readHandoffPendingFlag());
    }, []);

    useLayoutEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const currentUrl = new URL(window.location.href);
        const urlHandoffPayload = extractShopAuthHandoffPayload(currentUrl);
        const storedHandoffPayload = readShopAuthHandoffPayload();
        const handoffPayload = urlHandoffPayload ?? storedHandoffPayload;
        if (!handoffPayload) {
            return;
        }
        const { accessToken, refreshToken, partnerCode, cleanUrl, expiresIn } = handoffPayload;

        let incomingHandoffSource: string | null = null;
        if (urlHandoffPayload) {
            const hash = currentUrl.hash.startsWith('#')
                ? currentUrl.hash.slice(1)
                : currentUrl.hash;
            const hashParams = new URLSearchParams(hash);
            incomingHandoffSource = normalizePlayHandoffSource(
                hashParams.get('motito_handoff')
            );
            if (incomingHandoffSource === 'play') {
                setHasPlayHintFromHash(true);
            }
            persistShopAuthHandoffPayload(urlHandoffPayload);

            const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            if (urlHandoffPayload.cleanUrl !== currentPath) {
                window.history.replaceState(window.history.state, '', urlHandoffPayload.cleanUrl);
            }
        } else {
            incomingHandoffSource = readStoredHandoffSource();
        }

        let cancelled = false;
        const owner = authStore.beginSessionReplacement();
        setIsAuthHandoffProcessing(true);

        const scrubHashAndReload = () => {
            if (cancelled) {
                return;
            }
            clearShopAuthHandoffPayload();
            if (incomingHandoffSource) {
                const payload = JSON.stringify({ source: incomingHandoffSource, ts: Date.now() });
                window.sessionStorage.setItem(SHOP_HANDOFF_SOURCE_KEY, payload);
                setHandoffSource(incomingHandoffSource);
            } else {
                window.sessionStorage.removeItem(SHOP_HANDOFF_SOURCE_KEY);
                setHandoffSource(null);
            }
            window.sessionStorage.setItem(SHOP_HANDOFF_PENDING_KEY, '1');
            window.location.replace(cleanUrl);
        };

        void (async () => {
            let completed = false;
            try {
                const response = await writeAuthCookies(owner, () => fetch('/api/auth/set-session', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        access_token: accessToken,
                        refresh_token: refreshToken || undefined,
                        partner_code: partnerCode || undefined,
                        expires_in: expiresIn,
                    }),
                }));
                if (!response.ok) {
                    if (response.status >= 400 && response.status < 500) {
                        clearShopAuthHandoffPayload();
                    }
                    if (!cancelled) {
                        setIsAuthHandoffProcessing(false);
                    }
                    return;
                }
                completed = true;
                scrubHashAndReload();
            } catch {
                if (!cancelled) {
                    setIsAuthHandoffProcessing(false);
                }
            } finally {
                if (!completed && !cancelled && owner === currentAuthOwner()) {
                    await authStore.recoverServerSession();
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authStore]);

    useEffect(() => {
        const exitAsUnauthenticated = () => {
            const shouldKeepPlayAttribution =
                handoffSource === 'play'
                && (isAuthHandoffProcessing || hasHandoffPending || hasPlayHint);
            setIsOnboardingGateChecking(false);
            clearHandoffPending();
            if (!shouldKeepPlayAttribution) {
                clearHandoffSource();
            }
        };

        if (!authStore.isHydrated) {
            setIsOnboardingGateChecking(true);
            return;
        }
        if (!authStore.isAuthenticated) {
            exitAsUnauthenticated();
            return;
        }
        if (isPartnerAuthPath) {
            clearHandoffPending();
            setIsOnboardingGateChecking(false);
            return;
        }

        let cancelled = false;
        setIsOnboardingGateChecking(true);

        const preserveCurrentDestination = () => {
            if (typeof window !== 'undefined') {
                storePostAuthRedirect(
                    `${window.location.pathname}${window.location.search}${window.location.hash}`
                );
            }
        };

        const enforceOnboardingGate = async () => {
            // Run hydration + status refresh in parallel to reduce spinner
            // time on cross-domain handoff. These calls target different
            // endpoints and are safe to reconcile together.
            const [, firstStatusAttempt] = await Promise.all([
                authStore.ensureCurrentUserHydrated(),
                authStore.refreshRegistrationStatus(),
            ]);
            if (cancelled) {
                return;
            }
            if (!authStore.isAuthenticated) {
                exitAsUnauthenticated();
                return;
            }

            const resolveRegistrationStatus = async () => {
                if (authStore.registrationStatus) {
                    return authStore.registrationStatus;
                }

                let resolved = firstStatusAttempt ?? authStore.registrationStatus;
                if (resolved) {
                    return resolved;
                }

                // If the dedicated status endpoint blips, refresh `/users/me`
                // immediately rather than waiting on a fixed timer.
                await authStore.refreshCurrentUser();
                resolved = authStore.registrationStatus;
                if (resolved) {
                    return resolved;
                }

                // Final bounded retry to smooth over eventual consistency
                // right after cross-domain session handoff.
                return authStore.refreshRegistrationStatus();
            };

            const status = await resolveRegistrationStatus();

            if (cancelled) {
                return;
            }
            if (!authStore.isAuthenticated) {
                exitAsUnauthenticated();
                return;
            }

            if (!status) {
                const isOnboardingPath = pathname?.startsWith(
                    ONBOARDING_COMPLETE_PROFILE_PATH
                );
                const diagnostic = authStore.registrationStatusDiagnostic;
                const hasCoreProfile = authStore.hasCoreOnboardingProfileData;
                const isFetchFailure = diagnostic?.reason === 'fetch_failed';
                const isPlayTrafficContext =
                    handoffSource === 'play' ||
                    hasHandoffPending ||
                    hasPlayHintFromHash ||
                    isAuthHandoffProcessing;
                withScope((scope) => {
                    scope.setLevel('warning');
                    scope.setTag('surface', 'auth-gate');
                    scope.setContext('registration_status_unresolved', {
                        path: pathname,
                        reason: diagnostic?.reason ?? 'unknown',
                        source: diagnostic?.source ?? 'layout',
                        detail: diagnostic?.detail,
                    });
                    captureMessage('[AuthGate] Registration status unresolved; enforcing onboarding path');
                });

                if (!isOnboardingPath) {
                    if (isFetchFailure && hasCoreProfile && !isPlayTrafficContext) {
                        setIsOnboardingGateChecking(false);
                        clearHandoffPending();
                        clearHandoffSource();
                        return;
                    }
                    preserveCurrentDestination();
                    router.replace(ONBOARDING_COMPLETE_PROFILE_PATH);
                    return;
                }

                if (isFetchFailure && hasCoreProfile && !isPlayTrafficContext) {
                    clearHandoffSource();
                    router.replace('/');
                    return;
                }

                setIsOnboardingGateChecking(false);
                clearHandoffPending();
                return;
            }

            const isOnboardingPath = pathname?.startsWith(
                ONBOARDING_COMPLETE_PROFILE_PATH
            );
            const isFullyOnboarded = authStore.isFullyOnboardedWithProfile(status);

            if (!isFullyOnboarded && !isOnboardingPath) {
                preserveCurrentDestination();
                router.replace(ONBOARDING_COMPLETE_PROFILE_PATH);
                return;
            }

            setIsOnboardingGateChecking(false);
            clearHandoffPending();
            if (!isOnboardingPath || isFullyOnboarded) {
                clearHandoffSource();
            }
        };

        void enforceOnboardingGate().catch((error) => {
            if (!cancelled) {
                const isOnboardingPath = pathname?.startsWith(
                    ONBOARDING_COMPLETE_PROFILE_PATH
                );
                withScope((scope) => {
                    scope.setTag('surface', 'auth-gate');
                    scope.setContext('onboarding_gate_failure', {
                        path: pathname,
                    });
                    captureException(
                        error instanceof Error
                            ? error
                            : new Error('[AuthGate] unknown onboarding gate failure')
                    );
                });
                clearHandoffPending();
                if (!isOnboardingPath) {
                    preserveCurrentDestination();
                    router.replace(ONBOARDING_COMPLETE_PROFILE_PATH);
                    return;
                }
                setIsOnboardingGateChecking(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        authStore,
        authStore.isAuthenticated,
        authStore.isHydrated,
        authStore.registrationStatus,
        pathname,
        router,
        clearHandoffPending,
        clearHandoffSource,
        handoffSource,
        hasHandoffPending,
        hasPlayHint,
        hasPlayHintFromHash,
        isAuthHandoffProcessing,
        isPartnerAuthPath,
    ]);

    // Hold pre-hydration only for play handoff traffic so public pages
    // render immediately for anonymous visitors.
    const shouldHoldForUnhydratedPlayHandoff =
        !authStore.isHydrated &&
        (
            hasHandoffPending ||
            isAuthHandoffProcessing ||
            handoffSource === 'play'
        );

    const isAuthenticatedNeedsGateResolution =
        authStore.isAuthenticated &&
        !authStore.isFullyOnboardedWithProfile();

    // Hold content while onboarding gating resolves for authenticated users.
    const shouldHoldContent =
        isAuthHandoffProcessing ||
        hasHandoffPending ||
        shouldHoldForUnhydratedPlayHandoff ||
        (isAuthenticatedNeedsGateResolution &&
            (authStore.isLoading || isOnboardingGateChecking));
    const isPlayHandoffLoading =
        shouldHoldContent &&
        (
            isAuthHandoffProcessing ||
            hasHandoffPending ||
            handoffSource === 'play'
        );

    return (
        <div className="flex min-h-screen flex-col bg-background">
            {!isPlayHandoffLoading && !isCompleteProfilePath ? (
                <>
                    {/* Dynamic partner banner - only shows on partner domains */}
                    <PartnerBanner />
                    {/* Header handles its own sticky positioning */}
                    <Header />
                    <Suspense fallback={null}>
                        <WalletLinkEntry ready={!shouldHoldContent && !isOnboardingGateChecking} />
                        <ProgramLinkStateStrip />
                    </Suspense>
                </>
            ) : null}

            <div className="flex-1">
                {shouldHoldContent ? (
                    isPlayHandoffLoading ? (
                        <div
                            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-4 bg-background px-6 text-xl"
                            role="status"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <ShimmerLogoSVG
                                size="bootstrap"
                                animate={false}
                                className="opacity-95"
                                fillColor="#BDBDBD"
                                strokeColor="#BDBDBD"
                            />
                            <p className="text-center text-sm font-medium text-slate-500">
                                Preparing your account…
                            </p>
                        </div>
                    ) : (
                        <div
                            className="fixed inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-background px-6 text-xl"
                            role="status"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <ShimmerLogoSVG
                                size="bootstrap"
                                animate={false}
                                className="opacity-95"
                                fillColor="#BDBDBD"
                                strokeColor="#BDBDBD"
                            />
                            <p className="text-center text-sm text-muted-foreground">
                                Loading…
                            </p>
                        </div>
                    )
                ) : (
                    <div
                        className={
                            isCompleteProfilePath
                                ? 'min-h-screen bg-background'
                                : 'container min-h-[70vh] pt-5 text-xl max-sm:p-3 [&_p]:my-1'
                        }
                    >
                        {props.children}
                    </div>
                )}
                {pathname?.includes('/checkout') ||
                isPlayHandoffLoading ||
                isCompleteProfilePath ? null : <Footer />}
            </div>
        </div>
    );
});
